import React, { useState, useEffect } from 'react';
import Layout from '../common/Layout';
import LeagueCard from './LeagueCard';
import LeagueFilters from './LeagueFilters';
import WeekSelector from './WeekSelector';
import { getAvailableLeagues, joinLeague, getCurrentUser, getUserTeams } from '../../DatabaseAPI';
import './LeagueSelection.css';

function LeagueSelection({ user, onJoinLeague, onBack }) {
    const [leagues, setLeagues] = useState([]);
    const [filteredLeagues, setFilteredLeagues] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(1);
    const [filters, setFilters] = useState({
        leagueType: 'PUBLIC', // PUBLIC or PRIVATE or ALL
        status: 'OPEN', // OPEN, LOCKED, COMPLETED or ALL
        searchTerm: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userStats, setUserStats] = useState({
        currentLeagueCount: 0,
        maxLeaguesAllowed: 10,
        betaKeyValid: true
    });

    // Get current NFL week on component mount
    useEffect(() => {
        fetchCurrentWeek();
        fetchUserStats();
    }, []);

    // Fetch leagues when week or filters change
    useEffect(() => {
        if (selectedWeek) {
            fetchAvailableLeagues();
        }
    }, [selectedWeek, filters]);

    const fetchCurrentWeek = async () => {
        try {
            // For now, use Week 15 as the current week (as set in our league templates)
            const currentWeek = 15;
            setCurrentWeek(currentWeek);
            setSelectedWeek(currentWeek);
        } catch (err) {
            console.error('Error fetching current week:', err);
            setCurrentWeek(15);
            setSelectedWeek(15);
        }
    };

    const fetchUserStats = async () => {
        try {
            const currentUser = await getCurrentUser();
            const userTeams = await getUserTeams();
            
            if (currentUser && currentUser.profile) {
                setUserStats({
                    currentLeagueCount: userTeams.teams ? userTeams.teams.length : 0,
                    maxLeaguesAllowed: currentUser.profile.max_leagues_allowed || 10,
                    betaKeyValid: currentUser.profile.beta_key_used ? true : false
                });
            } else {
                // Fallback if user not properly authenticated
                setUserStats({
                    currentLeagueCount: 0,
                    maxLeaguesAllowed: 10,
                    betaKeyValid: false
                });
            }
        } catch (err) {
            console.error('Error fetching user stats:', err);
            setUserStats({
                currentLeagueCount: 0,
                maxLeaguesAllowed: 10,
                betaKeyValid: false
            });
        }
    };

    const fetchAvailableLeagues = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Create filters object for API call
            const apiFilters = {
                type: filters.leagueType !== 'ALL' ? filters.leagueType : undefined,
                week: selectedWeek
            };
            
            const response = await getAvailableLeagues(apiFilters);
            
            if (response && response.leagues) {
                const leagues = response.leagues.map(league => ({
                    id: league.id,
                    name: league.name,
                    leagueType: league.league_type,
                    status: league.status,
                    currentTeams: league.team_count || 0,
                    maxTeams: league.max_users || 12,
                    // entryFee / prizePool DELIBERATELY OMITTED — do NOT put numbers
                    // here. AvailableLeagueSerializer exposes no entry_fee or prize
                    // field at all (backend/game/api/serializers/auth_serializers.py:225),
                    // so any literal is invented. This previously read
                    // `entryFee: 0, // Currently all leagues are free`, which was
                    // simply false: the backend charges league.get_entry_cost() on
                    // join. Undefined makes LeagueCard render an explicit "—".
                    week: league.current_week,
                    year: league.season_year,
                    createdAt: league.created_at,
                    leagueCode: league.league_code
                }));
                
                setLeagues(leagues);
                applyFilters(leagues, filters);
            } else {
                setLeagues([]);
                setFilteredLeagues([]);
            }
        } catch (err) {
            setError('Failed to load leagues. Please try again.');
            console.error('Error fetching leagues:', err);
            setLeagues([]);
            setFilteredLeagues([]);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (leagueList, currentFilters) => {
        let filtered = [...leagueList];

        // Filter by league type
        if (currentFilters.leagueType && currentFilters.leagueType !== 'ALL') {
            filtered = filtered.filter(league => league.leagueType === currentFilters.leagueType);
        }

        // Filter by status
        if (currentFilters.status && currentFilters.status !== 'ALL') {
            filtered = filtered.filter(league => league.status === currentFilters.status);
        }

        // Filter by search term
        if (currentFilters.searchTerm) {
            filtered = filtered.filter(league => 
                league.name.toLowerCase().includes(currentFilters.searchTerm.toLowerCase())
            );
        }

        setFilteredLeagues(filtered);
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        applyFilters(leagues, newFilters);
    };

    const handleWeekChange = (week) => {
        setSelectedWeek(week);
    };

    const handleJoinLeague = async (leagueId, teamName) => {
        try {
            // If onJoinLeague prop is provided, use it (for testing/debug)
            if (onJoinLeague) {
                await onJoinLeague(leagueId);
            } else {
                // Use real API call
                const leagueData = {
                    league_id: leagueId,
                    team_name: teamName || `${user?.username || 'User'}'s Team`
                };
                
                await joinLeague(leagueData);
            }
            
            // Refresh leagues and user stats after joining
            await fetchAvailableLeagues();
            await fetchUserStats();
            
        } catch (err) {
            const errorMessage = err.message || 'Failed to join league. Please try again.';
            setError(errorMessage);
            console.error('Error joining league:', err);
        }
    };

    const canJoinMoreLeagues = () => {
        return userStats.currentLeagueCount < userStats.maxLeaguesAllowed && userStats.betaKeyValid;
    };

    return (
        <Layout title="Select League">
            <div className="league-selection">
                <div className="league-selection-header">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2>Join a League</h2>
                        {onBack && (
                            <button
                                onClick={onBack}
                                style={{
                                    backgroundColor: '#95a5a6',
                                    color: 'white',
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                ← Back to Login
                            </button>
                        )}
                    </div>
                    <div className="user-status">
                        <span className="league-count">
                            Leagues: {userStats.currentLeagueCount}/{userStats.maxLeaguesAllowed}
                        </span>
                        <span className={`beta-status ${userStats.betaKeyValid ? 'valid' : 'invalid'}`}>
                            {userStats.betaKeyValid ? '✓ Beta Access' : '✗ No Beta Access'}
                        </span>
                    </div>
                </div>

                <WeekSelector 
                    currentWeek={currentWeek}
                    selectedWeek={selectedWeek}
                    onWeekChange={handleWeekChange}
                />

                <LeagueFilters 
                    filters={filters}
                    onFiltersChange={handleFilterChange}
                />

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {!canJoinMoreLeagues() && (
                    <div className="warning-message">
                        {!userStats.betaKeyValid 
                            ? 'Beta access required to join leagues.'
                            : 'You have reached the maximum number of leagues allowed.'
                        }
                    </div>
                )}

                <div className="league-results">
                    {loading ? (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <span>Loading leagues...</span>
                        </div>
                    ) : filteredLeagues.length === 0 ? (
                        <div className="no-results">
                            <h3>No leagues found</h3>
                            <p>Try adjusting your filters or check back later.</p>
                        </div>
                    ) : (
                        <div className="league-grid">
                            {filteredLeagues.map(league => (
                                <LeagueCard 
                                    key={league.id}
                                    league={league}
                                    canJoin={canJoinMoreLeagues()}
                                    onJoin={handleJoinLeague}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default LeagueSelection;